import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { systemService } from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';

// Styled components
const SystemLogsContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const LogsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  color: ${({ theme }) => theme.colors.dark};
  margin: 0;
`;

const LogsCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const LogControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const LogFilters = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const LogTypeFilter = styled.select`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background-color: white;
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const LogLevelFilter = styled.select`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background-color: white;
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const SearchInput = styled.input`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  width: 200px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const LogsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${({ theme }) => theme.colors.lightAlt};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TableHeaderCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.lightAlt};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
`;

const TimeCell = styled(TableCell)`
  white-space: nowrap;
  width: 180px;
`;

const TypeCell = styled(TableCell)`
  width: 120px;
`;

const LevelCell = styled(TableCell)`
  width: 100px;
`;

const MessageCell = styled(TableCell)`
  word-break: break-word;
`;

const LogLevel = styled.span`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  text-transform: uppercase;
  background-color: ${({ level, theme }) => 
    level === 'error' ? `${theme.colors.error}20` :
    level === 'warning' ? `${theme.colors.warning}20` :
    level === 'info' ? `${theme.colors.info}20` :
    `${theme.colors.success}20`
  };
  color: ${({ level, theme }) => 
    level === 'error' ? theme.colors.error :
    level === 'warning' ? theme.colors.warning :
    level === 'info' ? theme.colors.info :
    theme.colors.success
  };
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const PageInfo = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.medium};
`;

const PageControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const PageButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background-color: ${({ active, theme }) => active ? theme.colors.primary : 'white'};
  color: ${({ active, theme }) => active ? theme.colors.white : theme.colors.medium};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.lightAlt};
  }
`;

const NoLogsMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  color: ${({ theme }) => theme.colors.medium};
  font-style: italic;
`;

const SystemLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: 'all',
    level: 'all',
    search: '',
    page: 1,
    limit: 10,
    sortBy: 'timestamp',
    sortOrder: 'DESC'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    logsPerPage: 10,
    totalPages: 1,
    total: 0
  });
  
  // Load logs from API
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        limit: filters.limit,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        ...(filters.type !== 'all' && { type: filters.type }),
        ...(filters.level !== 'all' && { level: filters.level }),
        ...(filters.search && { search: filters.search })
      };
      const response = await systemService.getSystemLogs(params);
      const data = response.data || {};
      const paginationData = data.pagination || {};
      setLogs(data.logs || []);
      setPagination({
        currentPage: paginationData.page ?? 1,
        logsPerPage: paginationData.limit ?? 10,
        totalPages: paginationData.totalPages ?? 1,
        total: paginationData.total ?? 0
      });
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch logs when component mounts or filters change
  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  
  const handleTypeFilterChange = (e) => {
    setFilters(prev => ({ ...prev, type: e.target.value, page: 1 }));
  };
  
  const handleLevelFilterChange = (e) => {
    setFilters(prev => ({ ...prev, level: e.target.value, page: 1 }));
  };
  
  const handleSearchChange = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };
  
  const handleClearLogs = async () => {
    if (window.confirm('Are you sure you want to clear all logs? This action cannot be undone.')) {
      try {
        const response = await systemService.clearSystemLogs({
          type: filters.type !== 'all' ? filters.type : undefined,
          level: filters.level !== 'all' ? filters.level : undefined
        });

        alert(`${(response.data && response.data.message) || 'Logs cleared successfully'}`);

        // Refresh logs
        fetchLogs();
      } catch (error) {
        console.error('Error clearing logs:', error);
        alert('Failed to clear logs. Please try again.');
      }
    }
  };
  
  const handleDownloadLogs = () => {
    // Generate a JSON file with current logs
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(logs, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `system-logs-${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };
  
  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };
  
  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };
  
  return (
    <SystemLogsContainer>
      <LogsHeader>
        <Title>System Logs</Title>
        <div>
          <Button variant="outline" onClick={handleDownloadLogs} style={{ marginRight: '8px' }}>
            Download Logs
          </Button>
          <Button variant="secondary" onClick={handleClearLogs}>
            Clear Logs
          </Button>
        </div>
      </LogsHeader>
      
      <LogsCard>
        <LogControls>
          <LogFilters>
            <LogTypeFilter value={filters.type} onChange={handleTypeFilterChange}>
              <option value="all">All Types</option>
              <option value="system">System</option>
              <option value="auth">Authentication</option>
              <option value="user">User</option>
              <option value="api">API</option>
              <option value="database">Database</option>
              <option value="support">Support (impersonation)</option>
              <option value="timereporting">Timereporting (audit)</option>
              <option value="integration_admin">Privileged administration (protected audit)</option>
              <option value="configuration_admin">Configuration (protected audit)</option>
            </LogTypeFilter>
            
            <LogLevelFilter value={filters.level} onChange={handleLevelFilterChange}>
              <option value="all">All Levels</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </LogLevelFilter>
          </LogFilters>
          
          <SearchInput 
            type="text" 
            placeholder="Search logs..." 
            value={filters.search}
            onChange={handleSearchChange}
          />
        </LogControls>
        
        {loading ? (
          <div>Loading logs...</div>
        ) : logs && logs.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
            <LogsTable>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Time</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Level</TableHeaderCell>
                  <TableHeaderCell>Message</TableHeaderCell>
                  <TableHeaderCell>User</TableHeaderCell>
                  {/* Extra audit detail columns (mainly for the Timereporting trail) */}
                  <TableHeaderCell>Customer</TableHeaderCell>
                  <TableHeaderCell>Employee</TableHeaderCell>
                  <TableHeaderCell>Date</TableHeaderCell>
                  <TableHeaderCell title="Employee-logged hours">Logged</TableHeaderCell>
                  <TableHeaderCell title="Customer-approved / contested hours">Contested</TableHeaderCell>
                  <TableHeaderCell title="Admin-settled / employee-agreed hours">Settled</TableHeaderCell>
                  <TableHeaderCell>Reason / Comment</TableHeaderCell>
                  <TableHeaderCell>IP</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {logs.map(log => {
                  const m = log.metadata || {};
                  const num = (v) => (v === 0 || (v != null && v !== '')) ? v : '';
                  const contested = num(m.hoursContested != null ? m.hoursContested : (m.hoursApproved != null ? m.hoursApproved : m.hoursSuggested));
                  const settled = num(m.hoursSettled != null ? m.hoursSettled : m.hoursAgreed);
                  return (
                    <TableRow key={log.id}>
                      <TimeCell>{formatTimestamp(log.timestamp)}</TimeCell>
                      <TypeCell>{log.type}</TypeCell>
                      <LevelCell>
                        <LogLevel level={log.level}>
                          {log.level}
                        </LogLevel>
                      </LevelCell>
                      <MessageCell>{log.message}</MessageCell>
                      <TableCell>{log.user || 'system'}</TableCell>
                      <TableCell>{m.customerName || ''}</TableCell>
                      <TableCell>{m.employeeName || ''}</TableCell>
                      <TableCell>{m.date || ''}</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>{num(m.hoursLogged)}</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>{contested}</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>{settled}</TableCell>
                      <TableCell>{m.reason || m.comment || ''}</TableCell>
                      <TableCell>{m.ip || ''}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </LogsTable>
            </div>

            <Pagination>
              <PageInfo>
                Showing {(pagination.currentPage - 1) * pagination.logsPerPage + 1}-{Math.min(pagination.currentPage * pagination.logsPerPage, pagination.total)} of {pagination.total} logs
              </PageInfo>
              
              <PageControls>
                <PageButton 
                  onClick={() => handlePageChange(1)} 
                  disabled={pagination.currentPage === 1}
                >
                  First
                </PageButton>
                <PageButton 
                  onClick={() => handlePageChange(pagination.currentPage - 1)} 
                  disabled={pagination.currentPage === 1}
                >
                  Prev
                </PageButton>
                
                {/* Generate page number buttons */}
                {[...Array(pagination.totalPages).keys()].map(page => {
                  // Show only a window of pages around the current page
                  const pageNum = page + 1;
                  if (
                    pageNum === 1 || 
                    pageNum === pagination.totalPages || 
                    (pageNum >= pagination.currentPage - 1 && pageNum <= pagination.currentPage + 1)
                  ) {
                    return (
                      <PageButton
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        active={pageNum === pagination.currentPage}
                      >
                        {pageNum}
                      </PageButton>
                    );
                  } else if (
                    pageNum === pagination.currentPage - 2 || 
                    pageNum === pagination.currentPage + 2
                  ) {
                    return <span key={pageNum}>...</span>;
                  } else {
                    return null;
                  }
                })}
                
                <PageButton 
                  onClick={() => handlePageChange(pagination.currentPage + 1)} 
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  Next
                </PageButton>
                <PageButton 
                  onClick={() => handlePageChange(pagination.totalPages)} 
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  Last
                </PageButton>
              </PageControls>
            </Pagination>
          </>
        ) : (
          <NoLogsMessage>No logs found matching the current filters.</NoLogsMessage>
        )}
      </LogsCard>
    </SystemLogsContainer>
  );
};

export default SystemLogs;
